import React from "react";

// Skeleton animation
const shimmer = "animate-pulse bg-gray-200";

export const TaskCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
    <div className={`h-5 w-3/4 ${shimmer} rounded mb-2`} />
    <div className={`h-4 w-1/2 ${shimmer} rounded mb-3`} />
    <div className="flex items-center gap-2 mb-2">
      <div className={`h-6 w-20 ${shimmer} rounded-full`} />
      <div className={`h-6 w-16 ${shimmer} rounded-full`} />
    </div>
    <div className={`h-3 w-full ${shimmer} rounded mb-1`} />
    <div className={`h-3 w-2/3 ${shimmer} rounded`} />
  </div>
);

export const TaskColumnSkeleton: React.FC = () => (
  <div className="flex-1 min-w-[300px]">
    <div className={`h-8 w-32 ${shimmer} rounded mb-4`} />
    <div className="space-y-3">
      <TaskCardSkeleton />
      <TaskCardSkeleton />
      <TaskCardSkeleton />
    </div>
  </div>
);

export const OKRCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
    <div className="flex items-start justify-between mb-3">
      <div className={`h-5 w-3/4 ${shimmer} rounded`} />
      <div className={`h-6 w-16 ${shimmer} rounded-full`} />
    </div>
    <div className={`h-4 w-1/2 ${shimmer} rounded mb-2`} />
    <div className="space-y-2 mt-3">
      <div className="flex items-center gap-2">
        <div className={`h-4 w-4 ${shimmer} rounded`} />
        <div className={`h-3 w-full ${shimmer} rounded`} />
      </div>
      <div className="flex items-center gap-2">
        <div className={`h-4 w-4 ${shimmer} rounded`} />
        <div className={`h-3 w-3/4 ${shimmer} rounded`} />
      </div>
    </div>
  </div>
);

export const FilterBarSkeleton: React.FC = () => (
  <div className="bg-white border-b border-gray-200 p-4">
    <div className="flex flex-wrap gap-3">
      <div className={`h-10 w-40 ${shimmer} rounded`} />
      <div className={`h-10 w-32 ${shimmer} rounded`} />
      <div className={`h-10 w-36 ${shimmer} rounded`} />
      <div className={`h-10 w-28 ${shimmer} rounded`} />
      <div className={`h-10 w-32 ${shimmer} rounded`} />
    </div>
  </div>
);

export const UnifiedGravityPlanSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50">
    <FilterBarSkeleton />
    <div className="flex gap-6 p-6">
      {/* Main content area */}
      <div className="flex-1">
        <div className="flex gap-4 overflow-x-auto pb-4">
          <TaskColumnSkeleton />
          <TaskColumnSkeleton />
          <TaskColumnSkeleton />
          <TaskColumnSkeleton />
        </div>
      </div>
      {/* OKR Panel */}
      <div className="w-96 bg-white rounded-lg shadow-sm p-4">
        <div className={`h-7 w-32 ${shimmer} rounded mb-4`} />
        <div className="space-y-3">
          <OKRCardSkeleton />
          <OKRCardSkeleton />
          <OKRCardSkeleton />
        </div>
      </div>
    </div>
  </div>
);

interface LoadingProgressProps {
  message: string;
  progress?: number;
  total?: number;
}

export const LoadingProgress: React.FC<LoadingProgressProps> = ({
  message,
  progress,
  total,
}) => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
        <svg
          className="animate-spin h-12 w-12 text-blue-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
      <p className="text-gray-600 text-lg font-medium">{message}</p>
      {progress !== undefined && total !== undefined && (
        <div className="mt-3">
          <div className="w-64 bg-gray-200 rounded-full h-2 mx-auto">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(progress / total) * 100}%` }}
            />
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {progress} of {total} loaded
          </p>
        </div>
      )}
    </div>
  </div>
);
